"use client";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faEnvelope,
	faGamepad,
	faHandshake,
	faPaperPlane,
	faScroll,
	faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const ContactPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	const contactMethods = [
		{
			id: "email",
			title: "Mensajero Mágico",
			icon: faEnvelope,
			description:
				"Un pergamino electrónico es la forma más segura de comunicarse a través de las dimensiones.",
			action: "Enviar un pergamino",
			link: "mailto:somos@losamosdelcalabozo.com?subject=Queridos Amos del Calabozo",
			color: "bg-blue-600 hover:bg-blue-700",
			hoverEffect: {
				scale: 1.05,
				boxShadow: "0 0 15px #3b82f6",
			},
		},
		{
			id: "whatsapp",
			title: "Cristal de comunicación",
			icon: faWhatsapp,
			description: "Para mensajes instantáneos entre reinos y dimensiones.",
			action: "Comunicar por cristal",
			link: `https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, quiero saber más sobre vuestra magia. ¿Podéis ayudarme?")}`,
			color: "bg-green-600 hover:bg-green-700",
			hoverEffect: {
				scale: 1.05,
				boxShadow: "0 0 15px #22c55e",
			},
		},
	];

	/* const magicQuotes = [
		"«Los Amos del Calabozo han convertido nuestra reunión semanal en una aventura inolvidable»",
		"«Nunca imaginé que podría vivir tantas emociones alrededor de una mesa»",
		"«La mejor forma de desconectar del mundo real es conectar con uno imaginario»",
		"«Desde que jugamos con ellos, la palabra 'dados' tiene un nuevo significado para nosotros»",
		"«Si piensas que la magia no existe, es que aún no has jugado una partida con Los Amos del Calabozo»",
	]; */

	const contactReasons = [
		{
			id: "celebraciones",
			title: "Celebraciones y Eventos",
			icon: faGamepad,
			description:
				"¿Cumpleaños? ¿Despedida? ¿Reunión de empresa? Una aventura diseñada a medida hará de cualquier celebración un recuerdo único.",
		},
		{
			id: "iniciacion",
			title: "Iniciación al Rol",
			icon: faScroll,
			description:
				"Si nunca has jugado y quieres descubrir este fascinante hobby, te guiaremos en tus primeros pasos con paciencia y entusiasmo.",
		},
		{
			id: "sesiones",
			title: "Sesiones Personalizadas",
			icon: faWandMagicSparkles,
			description:
				"Sea cual sea tu sistema favorito o la temática que prefieras, podemos crear una experiencia adaptada a tus deseos específicos.",
		},
	];

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/contacto_header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">Contacto</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">
							Puedes invocarnos con cuatro de maná
							<br />
							<span className="text-sm italic">
								(Pero si no tienes 2 genéricos, una blanco y otro azul, siempre puedes usar los métodos
								convencionales)
							</span>
						</strong>
					</motion.p>
				</div>
			</section>

			{/* Main Contact Methods */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="grid grid-cols-1 md:grid-cols-2 gap-8"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.5, duration: 0.6 }}
					>
						{contactMethods.map((method) => (
							<motion.div
								key={method.id}
								className="bg-gray-800 rounded-lg p-8 border-l-4 border-red-700 flex flex-col"
								whileHover={{ scale: 1.02, borderColor: "#f87171" }}
							>
								<div className="flex items-center mb-4">
									<div className="bg-red-600 p-4 rounded-full mr-4">
										<FontAwesomeIcon icon={method.icon} className="text-white text-2xl" />
									</div>
									<h3 className="text-2xl font-amarante headerFont text-red-400">{method.title}</h3>
								</div>
								<p className="text-gray-300 mb-6 flex-grow">{method.description}</p>
								<motion.a
									href={method.link}
									className={`${method.color} text-white px-6 py-3 rounded-lg font-bold text-center transition-colors`}
									whileHover={method.hoverEffect}
									whileTap={{ scale: 0.98 }}
									target={method.id === "whatsapp" ? "_blank" : ""}
									rel={method.id === "whatsapp" ? "noopener noreferrer" : ""}
								>
									<FontAwesomeIcon
										icon={method.id === "email" ? faPaperPlane : method.icon}
										className="mr-2"
									/>
									{method.action}
								</motion.a>
							</motion.div>
						))}
					</motion.div>

					{/* Email Address Display */}
					<motion.div
						className="mt-8 sm:mt-10 md:mt-12 bg-gray-800 rounded-lg p-4 sm:p-6 md:p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-xl sm:text-2xl font-amarante headerFont text-red-400 mb-4">
							Nuestro Pergamino Oficial
						</h3>
						<motion.div
							className="text-lg sm:text-2xl md:text-4xl font-bold text-white bg-gray-700 py-3 px-2 sm:px-4 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#3b82f6",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-xs sm:text-sm italic">
							Haz click para copiar a tu grimorio de hechizos (portapapeles)
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white text-sm py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Why Contact Us */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faHandshake} className="mr-3 text-red-500" />
						¿Por qué convocarnos?
					</motion.h2>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{contactReasons.map((reason, index) => (
							<motion.div
								key={reason.id}
								className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-700"
								initial={{ opacity: 0, x: -20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.2, duration: 0.6 }}
							>
								<div className="bg-red-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4">
									<FontAwesomeIcon icon={reason.icon} className="text-white text-2xl" />
								</div>
								<h3 className="text-xl font-amarante text-red-400 mb-2">{reason.title}</h3>
								<p className="text-gray-300">{reason.description}</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>
			{/* 
			{/* Magic Scroll / Testimonials }
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-8 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faScroll} className="mr-3 text-red-500" />
						Pergaminos de Aventureros
					</motion.h2>

					<motion.div
						className="relative overflow-hidden py-10"
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
					>
						<motion.div
							className="flex space-x-8"
							animate={{
								x: isHovering ? 0 : [-1000, 0],
							}}
							transition={{
								x: {
									repeat: Infinity,
									repeatType: "loop",
									duration: 30,
									ease: "linear",
								},
							}}
						>
							{magicQuotes.map((quote, index) => (
								<motion.div
									key={index}
									className="bg-gray-800 rounded-lg p-6 border-l-4 border-red-700 min-w-[300px] md:min-w-[400px]"
									whileHover={{ scale: 1.05, borderColor: "#f59e0b" }}
								>
									<p className="text-gray-300 italic">{quote}</p>
								</motion.div>
							))}
						</motion.div>
					</motion.div>
				</div>
			</section> */}

			{/* Final CTA */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="bg-gray-800 rounded-lg p-8 text-center border-4 border-red-700"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<motion.div
							whileHover={{ scale: 1.05 }}
							className="mx-auto w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mb-6"
						>
							<FontAwesomeIcon icon={faWandMagicSparkles} className="text-white text-4xl" />
						</motion.div>
						<h2 className="text-3xl font-amarante headerFont text-red-400 mb-4">¡La aventura aguarda!</h2>
						<p className="text-gray-300 text-xl mb-8">
							No importa si eres un novato o un veterano, si buscas emoción, diversión o una nueva forma de
							socializar... hay un calabozo esperándote.
						</p>
						<motion.div
							className="flex flex-col md:flex-row items-center justify-center gap-4"
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
						>
							<motion.a
								href="mailto:somos@losamosdelcalabozo.com?subject=Queridos Amos del Calabozo"
								className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.1,
									boxShadow: "0 0 15px #f93d3d",
									backgroundColor: "white",
									color: "black",
									fontWeight: "900",
									fontStyle: "italic",
								}}
								whileTap={{ scale: 0.98 }}
							>
								<FontAwesomeIcon icon={faEnvelope} className="mr-2" />
								Enviar Email
							</motion.a>

							<motion.a
								href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, quiero vivir una aventura con vosotros. ¿Cuándo empezamos?")}`}
								className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.05,
									boxShadow: "0 0 15px #22c55e",
								}}
								whileTap={{ scale: 0.98 }}
								target="_blank"
								rel="noopener noreferrer"
							>
								<FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
								WhatsApp
							</motion.a>
						</motion.div>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							Si susurras "Natural 20" tres veces frente a un espejo a medianoche, un Amo del Calabozo
							aparecerá para narrar tu pesadilla...
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default ContactPage;
